import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { sendTokenToExtension } from "@/helpers/send-to-extension.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions, { usePermissionCheck } from "@/hooks/use-permissions.hook.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { queryClient } from "@/queryClient.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useAuthStore } from "@/stores/employee.store.ts";
import { AppDistribution } from "@/types/common.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
} from "@floating-ui/react";
import { Box, Button, ButtonLink, FontAwesomeIcon, Stack, Title } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const SidebarUserMenu = () => {
  const { data: employee } = useEmployeeMe();
  const { setToken } = useAuthStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { renderEmployeeName } = useFormatter();
  const { hasPermissionsToViewSettings } = usePermissionCheck();
  const { canIEdit } = usePermissions();

  const [isOpen, setIsOpen] = useState(false);

  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context,
  } = useFloating({
    placement: "right-end",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(10), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, focus, dismiss]);

  const logout = () => {
    queryClient.clear();
    setToken(null);
    sendTokenToExtension(null);
    navigate("/login");
  };

  if (!employee) return null;

  return (
    <>
      {/* Своих отступов нет: меню живёт в слоте `footer` рельса, а вертикальные поля там его.
          С `my-4` низ рельса получал 32px вместо прежних 16px. */}
      <Box className={"w-8 h-8 cursor-pointer"} ref={setReference} {...getReferenceProps()}>
        <EmployeeAvatar employee={employee} size={"md"} />
      </Box>
      {isOpen && (
        <FloatingPortal>
          <Box
            ref={setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={"p-(--gap-smd) z-popover"}
          >
            <Stack gap={"smd"}>
              <Stack
                items={"center"}
                className={"bg-light rounded-sm shadow-sm w-48 p-4"}
                gap={"sm"}
              >
                <EmployeeAvatar size={"xl"} employee={employee} />
                <Title header={renderEmployeeName(employee)} className={"text-center"} />
                <ButtonLink
                  variant={"secondary"}
                  size={"sm"}
                  href={"/account-settings"}
                  onClick={() => {
                    setIsOpen(false);
                  }}
                >
                  {t("core:account_settings")}
                </ButtonLink>
              </Stack>
              <Stack
                items={"center"}
                className={"bg-light rounded-sm shadow-sm w-48 p-2"}
                gap={"sm"}
              >
                {import.meta.env.VITE_APP_DISTRIBUTION == AppDistribution.skailer && (
                  <ButtonLink
                    onClick={() => {
                      analyticsService.trackEvent(analyticEvents.pageAction, {
                        [analyticProperties.actionType]: "Help Clicked",
                        [analyticProperties.source]: "User Menu",
                      });
                      setIsOpen(false);
                    }}
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-question" />}
                    variant={"menu"}
                    href={"/help"}
                  >
                    {t("common:help_center")}
                  </ButtonLink>
                )}

                {canIEdit(ApiPermission.recruiting_module) && (
                  <ButtonLink
                    onClick={() => {
                      analyticsService.trackEvent(analyticEvents.pageAction, {
                        [analyticProperties.actionType]: "Extension Clicked",
                        [analyticProperties.source]: "User Menu",
                      });
                      setIsOpen(false);
                    }}
                    blank
                    leftIcon={<FontAwesomeIcon icon={"fa-brands fa-chrome"} />}
                    variant={"menu"}
                    href={import.meta.env.VITE_EXTENSION_URL}
                  >
                    {t("ats:chrome_extension")}
                  </ButtonLink>
                )}
                {hasPermissionsToViewSettings && (
                  <ButtonLink
                    href={"/settings"}
                    onClick={() => {
                      setIsOpen(false);
                    }}
                    variant={"menu"}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-gear"} />}
                  >
                    {t("common:settings")}
                  </ButtonLink>
                )}
                <Button
                  onClick={() => {
                    setIsOpen(false);
                    logout();
                  }}
                  variant={"menu"}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-right-from-bracket"} />}
                >
                  {t("common:logout")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </FloatingPortal>
      )}
    </>
  );
};
